import { useState, useEffect } from 'react'
import { SystemParameter, WithdrawalFeeConfig } from '@/types'
import '../styles/TabStyles.css'

interface WithdrawalFeeTabProps {
  parameters: SystemParameter[]
  onUpdate: (key: string, value: string) => Promise<void>
}

export default function WithdrawalFeeTab({ parameters, onUpdate }: WithdrawalFeeTabProps) {
  const [config, setConfig] = useState<WithdrawalFeeConfig>({ fee_rate: 0, platform_commission_rate: 0 })
  const [editing, setEditing] = useState(false)
  const [tempConfig, setTempConfig] = useState<WithdrawalFeeConfig>({ fee_rate: 0, platform_commission_rate: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load config from parameters
  useEffect(() => {
    const feeRateParam = parameters.find(p => p.param_key === 'withdrawal_fee_rate')
    const commissionParam = parameters.find(p => p.param_key === 'withdrawal_platform_commission')

    if (feeRateParam && commissionParam) {
      const newConfig = {
        fee_rate: parseFloat(feeRateParam.param_value),
        platform_commission_rate: parseFloat(commissionParam.param_value),
      }
      setConfig(newConfig)
      setTempConfig(newConfig)
    }
  }, [parameters])

  const handleEdit = () => {
    setTempConfig(config)
    setEditing(true)
  }

  const handleCancel = () => {
    setEditing(false)
    setError(null)
  }

  const handleSave = async () => {
    // Validation
    if (tempConfig.fee_rate < 0 || tempConfig.fee_rate > 100) {
      setError('手续费率必须在0-100之间')
      return
    }
    if (tempConfig.platform_commission_rate < 0 || tempConfig.platform_commission_rate > 100) {
      setError('平台分成必须在0-100之间')
      return
    }

    setLoading(true)
    try {
      // Update both parameters
      await onUpdate('withdrawal_fee_rate', tempConfig.fee_rate.toString())
      await onUpdate('withdrawal_platform_commission', tempConfig.platform_commission_rate.toString())
      setConfig(tempConfig)
      setEditing(false)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setLoading(false)
    }
  }

  const calculateExample = (amount: number) => {
    const fee = amount * (config.fee_rate / 100)
    const platformCommission = fee * (config.platform_commission_rate / 100)
    const operatorCommission = fee - platformCommission
    const netAmount = amount - fee

    return {
      fee,
      platformCommission,
      operatorCommission,
      netAmount,
    }
  }

  const example = calculateExample(1000)

  return (
    <div className="tab-panel">
      <div className="panel-header">
        <h3>提现手续费配置</h3>
        {!editing && (
          <button className="btn-primary" onClick={handleEdit}>
            编辑
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {editing ? (
        <div className="edit-form">
          <div className="form-group">
            <label>提现手续费率 (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={tempConfig.fee_rate}
              onChange={e => setTempConfig({ ...tempConfig, fee_rate: parseFloat(e.target.value) })}
              placeholder="输入手续费率"
            />
            <small>提现时收取的手续费百分比</small>
          </div>

          <div className="form-group">
            <label>平台分成比例 (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={tempConfig.platform_commission_rate}
              onChange={e => setTempConfig({ ...tempConfig, platform_commission_rate: parseFloat(e.target.value) })}
              placeholder="输入平台分成比例"
            />
            <small>平台从手续费中获得的比例</small>
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
              取消
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      ) : (
        <div className="config-display">
          <div className="config-item">
            <label>提现手续费率</label>
            <div className="config-value">{config.fee_rate.toFixed(2)}%</div>
          </div>

          <div className="config-item">
            <label>平台分成比例</label>
            <div className="config-value">{config.platform_commission_rate.toFixed(2)}%</div>
          </div>

          {/* Example Calculation */}
          <div className="example-section">
            <h4>计算示例 (提现 ¥1000)</h4>
            <div className="example-grid">
              <div className="example-item">
                <span className="label">手续费</span>
                <span className="value">¥{example.fee.toFixed(2)} ({config.fee_rate.toFixed(2)}%)</span>
              </div>
              <div className="example-item">
                <span className="label">平台分成</span>
                <span className="value">¥{example.platformCommission.toFixed(2)} ({config.platform_commission_rate.toFixed(2)}%)</span>
              </div>
              <div className="example-item">
                <span className="label">运营商分成</span>
                <span className="value">¥{example.operatorCommission.toFixed(2)} ({(100 - config.platform_commission_rate).toFixed(2)}%)</span>
              </div>
              <div className="example-item highlight">
                <span className="label">用户实际到账</span>
                <span className="value">¥{example.netAmount.toFixed(2)} ({((example.netAmount / 1000) * 100).toFixed(2)}%)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
